import { Alert, Box, Button, Chip, Divider, Grid, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { config } from '~/config';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useSelector } from 'react-redux';
import ItineraryOrderSummary from './ItineraryOrderSummary';
import { STATUS } from '~/literals';
import { RootState } from '~/types';
import { trackButtonClick, trackLinkClick } from '~/utils/analytics';

type Props = {
  paymentResult?: 'success' | 'failed' | 'canceled';
};

function ItineraryFinalStage({ paymentResult }: Props) {
  const { finalItinerary, pageStatus, errorMessage, orderId } = useSelector((state: RootState) => state.itineraryWizard);
  const order = finalItinerary?.order || finalItinerary;
  const reference = order?.orderId || orderId;
  const paymentStatus = String(order?.paymentStatus || '').toLowerCase();
  const isPaid = paymentStatus === 'paid';
  const isPending = paymentStatus === 'pending';
  const isCanceled = paymentResult === 'canceled';
  const isFailed = paymentResult === 'failed' || (!!paymentStatus && !isPaid && !isPending);

  if (isCanceled) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <ErrorOutlineIcon color="warning" sx={{ fontSize: 76 }} />
          <Typography variant="h4" fontWeight={700} color="warning.main">
            Payment was cancelled
          </Typography>
          <Typography variant="body1" color="text.secondary" maxWidth={720}>
            You cancelled the payment before it was completed. No payment confirmation check is required for this order.
          </Typography>
          {reference ? (
            <Chip color="default" label={`Order reference: ${reference}`} sx={{ fontWeight: 700 }} />
          ) : null}
          <Button variant="contained" href="/itinerary" onClick={() => trackButtonClick('ItineraryFinalStage', 'Start again')}>
            Start again
          </Button>
        </Stack>
      </Paper>
    );
  }

  if (pageStatus === STATUS.RUNNING) {
    return (
      <Grid container spacing={2}>
        {[140, 220, 120].map((height, index) => (
          <Grid item xs={12} key={index}>
            <Skeleton variant="rounded" width="100%" height={height} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (pageStatus === STATUS.ERROR || isFailed) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <ErrorOutlineIcon color="error" sx={{ fontSize: 76 }} />
          <Typography variant="h4" fontWeight={700} color="error">
            We couldn't confirm your payment
          </Typography>
          <Typography variant="body1" color="text.secondary" maxWidth={720}>
            {errorMessage || 'Your itinerary order is not marked as paid. If you believe payment was taken, please ask for help with your order reference and proof of payment.'}
          </Typography>
          {reference ? (
            <Chip color="default" label={`Order reference: ${reference}`} sx={{ fontWeight: 700 }} />
          ) : null}
          <Alert severity="info" sx={{ width: '100%', maxWidth: 720, textAlign: 'left' }}>
            Ask for help and include your order reference, payment confirmation, and the email address used for the order.
          </Alert>
          <Button variant="contained" href={`${config.app.helpCenterLink}/ask-for-help/`} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick('ItineraryFinalStage', 'Ask for help - payment failed', `${config.app.helpCenterLink}/ask-for-help/`)}>
            Ask for help
          </Button>
        </Stack>
      </Paper>
    );
  }

  if (isPending || !isPaid) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <HourglassTopIcon color="primary" sx={{ fontSize: 76 }} />
          <Typography variant="h4" fontWeight={700}>
            We're confirming your payment
          </Typography>
          <Typography variant="body1" color="text.secondary" maxWidth={720}>
            Payment confirmation is taking a little longer than expected. Please refresh this page shortly. If you have already paid and the status does not update, ask for help with your order reference.
          </Typography>
          {reference ? <Chip label={`Order reference: ${reference}`} /> : null}
          <Button variant="outlined" href={`${config.app.helpCenterLink}/ask-for-help/`} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick('ItineraryFinalStage', 'Ask for help - payment pending', `${config.app.helpCenterLink}/ask-for-help/`)}>
            Ask for help
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      {order?.fulfillmentStatus === 'failed' ? <Alert severity="warning">Payment is confirmed, but itinerary processing needs attention. Please contact support with reference {reference}. Do not pay again.</Alert> : null}
      {order?.reservation?.status === 'ready' ? <Alert severity="success">Your reservation is ready. {order.reservation.airlineReference ? `Airline reference: ${order.reservation.airlineReference}` : ''}</Alert> : null}
      <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success" sx={{ alignItems: 'center', py: 2 }}>
        <Typography variant="h6" fontWeight={700}>Payment confirmed — your itinerary order is in progress.</Typography>
        <Typography variant="body2">Thank you for choosing Tripenger. We'll email you once your itinerary is ready.</Typography>
      </Alert>

      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={8}>
            <Stack spacing={1.5}>
              <Typography variant="h4" fontWeight={700}>
                Your itinerary request is confirmed
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Our travel team has received your request and will prepare your itinerary within the selected package timeframe.
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body1">Order reference: <strong>{reference}</strong></Typography>
                <Typography variant="body1">Package: <strong>{order?.packageTitle || order?.packageId || '-'}</strong></Typography>
                <Typography variant="body1">Payment status: <Chip size="small" color="success" label="Paid" sx={{ ml: 1 }} /></Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f6f9ff', height: '100%' }}>
              <Stack spacing={1}>
                <MailOutlineIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>What happens next?</Typography>
                <Typography variant="body2" color="text.secondary">
                  We'll send updates to <strong>{order?.customerEmail || 'your email address'}</strong>. Keep your order reference handy if you need to ask for help.
                </Typography>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <ItineraryOrderSummary searchParams={order?.searchParams} />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" href="/itinerary" onClick={() => trackButtonClick('ItineraryFinalStage', 'Create another itinerary')}>
              Create another itinerary
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  );
}

export default ItineraryFinalStage;